import { Component, effect, inject, input, output, signal, ChangeDetectionStrategy } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';

import { Empresa } from '../../../models/empresa.model';
import { EmpresaService } from '../../../services/empresa.service';
import { Anuncio } from '../../../models/anuncio.model';
import { EmpresaUpdate } from '../../../models/empresa.model';

const SISTEMAS_DISPONIBLES = [
  { key: 'POS', label: 'POS (Punto de Venta)' },
  { key: 'COMANDAS', label: 'Comandas' },
  { key: 'FACTURACION', label: 'Facturación electrónica' },
  { key: 'CONTROLADOR_TIEMPO', label: 'Controlador de Tiempo' },
  { key: 'MICA_CHAT', label: 'Mica Chat' },
  { key: 'CAMPANAS', label: 'Campañas' },
  { key: 'PROMOCION_IMPRESORA', label: 'Promoción Impresora' },
  { key: 'PIDELO', label: 'Pidelo (Carta digital)' },
];

@Component({
  selector: 'app-modal-editar',
  imports: [ReactiveFormsModule],
  templateUrl: './modal-editar.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './modal-editar.component.css',
})
export class ModalEditarComponent {
  modal = input.required<any>();
  empresa = input.required<Empresa | null>();
  editSuccess = output<void>();
  editError = output<string>();

  private empresaService = inject(EmpresaService);
  private formBuilder = inject(FormBuilder);

  sistemasActivos = signal<string[]>([]);
  sistemasDisponibles = SISTEMAS_DISPONIBLES;

  formEditar = this.formBuilder.group({
    nombre: ['', Validators.required],
    direccion: [''],
    telefono: [''],
    token_sunat: [''],
    maximo_mesas: [0, Validators.min(0)],
    maximo_gestion_tiempos: [0, Validators.min(0)],
    maximo_sucursales: [0, Validators.min(0)],
    maximo_telefonos: [0, Validators.min(0)],
    pidelo_endpoint: ['', [Validators.pattern(/^https?:\/\/[^\s/]+\/[^\s]+$/i)]],
    vencimiento: ['', Validators.required],
    anuncios: this.formBuilder.array([]),
  });

  constructor() {
    effect(() => {
      this.loadEmpresaData();
    });
  }

  get anunciosFormArray(): FormArray {
    return this.formEditar.get('anuncios') as FormArray;
  }

  get anuncios(): Anuncio[] {
    const data = this.anunciosFormArray.value;
    return Array.isArray(data) ? data : [];
  }

  toggleSistema(key: string) {
    this.sistemasActivos.update((list) =>
      list.includes(key) ? list.filter((s) => s !== key) : [...list, key],
    );
  }

  isSistemaActivo(key: string): boolean {
    return this.sistemasActivos().includes(key);
  }

  private loadEmpresaData() {
    const empresa = this.empresa();
    if (empresa) {
      let fechaFormatted = '';
      if (empresa?.licencia) {
        const fecha = new Date(empresa.licencia.fecha_fin);
        if (!isNaN(fecha.getTime())) {
          fechaFormatted = fecha.toISOString().split('T')[0];
        }
      }

      while (this.anunciosFormArray.length !== 0) {
        this.anunciosFormArray.removeAt(0);
      }

      if (empresa?.anuncios) {
        empresa.anuncios.forEach((anuncio) => {
          this.addAnuncio(anuncio.titulo, anuncio.contenido);
        });
      }

      this.formEditar.patchValue({
        nombre: empresa?.nombre || '',
        direccion: empresa?.direccion || '',
        telefono: empresa?.telefono || '',
        token_sunat: empresa?.token_sunat || '',
        maximo_mesas: Number(empresa?.maximo_mesas ?? 0),
        maximo_gestion_tiempos: Number(empresa?.maximo_gestion_tiempos ?? 0),
        maximo_sucursales: Number(empresa?.maximo_sucursales ?? 0),
        maximo_telefonos: Number(empresa?.maximo_telefonos ?? 0),
        pidelo_endpoint: empresa?.pidelo_endpoint || '',
        vencimiento: fechaFormatted,
      });

      this.sistemasActivos.set(empresa?.sistemas_activos ?? []);
    }
  }

  addAnuncio(titulo: string, contenido: string) {
    this.anunciosFormArray.push(
      this.formBuilder.group({
        titulo: [titulo, Validators.required],
        contenido: [contenido, Validators.required],
      }),
    );
  }

  delAnuncio(index: number) {
    this.anunciosFormArray.removeAt(index);
  }

  saveChanges() {
    if (this.formEditar.valid) {
      const formData = this.formEditar.value;
      const empresa = this.empresa();

      if (empresa) {
        const updatePayload = this.buildUpdatePayload(formData);
        const changedPayload = this.getChangedPayload(empresa, updatePayload);

        if (Object.keys(changedPayload).length === 0) {
          this.editError.emit('No hay cambios para guardar');
          return;
        }

        this.empresaService
          .editEmpresa({
            id: empresa.id,
            ...changedPayload,
          })
          .subscribe({
            next: () => {
              this.editSuccess.emit();
              this.modal().close();
            },
            error: (error) => {
              this.editError.emit(error.message || 'Error al editar empresa');
            },
          });
      } else {
        this.editError.emit('No se encontró empresa para editar');
      }
    } else {
      this.editError.emit('Formulario inválido');
    }
  }

  private buildUpdatePayload(formData: any): EmpresaUpdate {
    return {
      nombre: (formData.nombre || '').trim(),
      direccion: (formData.direccion || '').trim(),
      telefono: (formData.telefono || '').trim() || undefined,
      token_sunat: (formData.token_sunat || '').trim() || undefined,
      maximo_mesas: Number(formData.maximo_mesas || 0),
      maximo_gestion_tiempos: Number(formData.maximo_gestion_tiempos || 0),
      maximo_sucursales: Number(formData.maximo_sucursales || 0),
      maximo_telefonos: Number(formData.maximo_telefonos || 0),
      sistemas_activos: this.sistemasActivos(),
      pidelo_endpoint: (formData.pidelo_endpoint || '').trim() || null,
      anuncios: this.normalizeAnuncios(this.anuncios),
      vencimiento: formData.vencimiento || undefined,
    };
  }

  private getChangedPayload(empresa: Empresa, payload: EmpresaUpdate): EmpresaUpdate {
    const original = this.buildOriginalPayload(empresa);
    const changed: EmpresaUpdate = {};

    (Object.keys(payload) as Array<keyof EmpresaUpdate>).forEach((key) => {
      const currentValue = payload[key];
      const originalValue = original[key];

      if (Array.isArray(currentValue) && Array.isArray(originalValue)) {
        if (JSON.stringify(currentValue) !== JSON.stringify(originalValue)) {
          changed[key] = currentValue as never;
        }
        return;
      }

      if (currentValue !== originalValue) {
        changed[key] = currentValue as never;
      }
    });

    return changed;
  }

  private buildOriginalPayload(empresa: Empresa): EmpresaUpdate {
    return {
      nombre: (empresa.nombre || '').trim(),
      direccion: (empresa.direccion || '').trim(),
      telefono: (empresa.telefono || '').trim() || undefined,
      token_sunat: (empresa.token_sunat || '').trim() || undefined,
      maximo_mesas: Number(empresa.maximo_mesas ?? 0),
      maximo_gestion_tiempos: Number(empresa.maximo_gestion_tiempos ?? 0),
      maximo_sucursales: Number(empresa.maximo_sucursales ?? 0),
      maximo_telefonos: Number(empresa.maximo_telefonos ?? 0),
      sistemas_activos: empresa.sistemas_activos ?? [],
      pidelo_endpoint: (empresa.pidelo_endpoint || '').trim() || null,
      anuncios: this.normalizeAnuncios(empresa.anuncios || []),
      vencimiento: this.formatVencimiento(empresa),
    };
  }

  private normalizeAnuncios(anuncios: Anuncio[]): Anuncio[] {
    return (Array.isArray(anuncios) ? anuncios : []).map((anuncio) => ({
      titulo: (anuncio?.titulo || '').trim(),
      contenido: (anuncio?.contenido || '').trim(),
    }));
  }

  private formatVencimiento(empresa: Empresa): string | undefined {
    if (!empresa?.licencia?.fecha_fin) {
      return undefined;
    }
    const fecha = new Date(empresa.licencia.fecha_fin);
    if (isNaN(fecha.getTime())) {
      return undefined;
    }
    return fecha.toISOString().split('T')[0];
  }
}
